import { theme } from '@/config'
import { useLayoutEffect } from 'react'

interface UseAutoHeightResizeOptions {
  defaultHeight?: number
  maxHeight?: number
  className?: string
  minHeight?: number
  padding?: number
}

export const useAutoHeightResize = (
  textareaRef: React.RefObject<HTMLDivElement>,
  options: UseAutoHeightResizeOptions = {},
) => {
  const {
    defaultHeight = theme.size.medium,
    minHeight = theme.size.medium,
    maxHeight,
    className,
    padding,
  } = options

  useLayoutEffect(() => {
    const setupAutoResize = () => {
      const textareaContainer = textareaRef.current
      if (!textareaContainer) return

      const textarea = textareaContainer.querySelector(className) as HTMLTextAreaElement
      if (!textarea) return

      textarea.style.minHeight = `${minHeight}px`

      const autoResize = () => {
        textarea.style.height = 'auto'

        let scrollHeight = textarea.scrollHeight

        if (!textarea.value || textarea.value.trim() === '') {
          scrollHeight = defaultHeight
        } else {
          const hasLineBreaks = textarea.value.includes('\n')
          const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || minHeight

          if (!hasLineBreaks) {
            const tempSpan = document.createElement('span')
            tempSpan.style.font = getComputedStyle(textarea).font
            tempSpan.style.whiteSpace = 'nowrap'
            tempSpan.style.visibility = 'hidden'
            tempSpan.style.position = 'absolute'
            tempSpan.textContent = textarea.value
            document.body.appendChild(tempSpan)

            const textWidth = tempSpan.offsetWidth
            const textareaWidth = textarea.clientWidth - padding

            document.body.removeChild(tempSpan)

            if (textWidth > textareaWidth) {
              scrollHeight = Math.max(scrollHeight, lineHeight)
            } else {
              scrollHeight = lineHeight
            }
          } else {
            scrollHeight = Math.max(scrollHeight, lineHeight)
          }
        }

        const finalHeight = Math.min(scrollHeight, maxHeight)
        textarea.style.height = finalHeight + 'px'
      }

      const observer = new MutationObserver(() => {
        autoResize()
      })

      observer.observe(textarea, {
        attributes: true,
        attributeFilter: ['value'],
        childList: true,
        subtree: true,
        characterData: true,
      })

      let lastValue = textarea.value
      const checkValueChange = () => {
        if (textarea.value !== lastValue) {
          lastValue = textarea.value
          autoResize()
        }
      }
      const intervalId = setInterval(checkValueChange, 100)

      autoResize()

      return () => {
        observer?.disconnect?.()
        clearInterval(intervalId)
      }
    }

    const timeoutId = setTimeout(setupAutoResize, 100)
    return () => clearTimeout(timeoutId)
  }, [])
}
